import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Container, Stack, Card, Group, Text, Button, 
  Badge, Divider, Alert, Transition, Modal, ActionIcon,
  Tooltip, Box, RingProgress, Switch 
} from '@mantine/core';
import { 
  IconUpload, IconCheck, IconAlertCircle, 
  IconFileText, IconBrain, IconShield, IconTrash,
  IconLoader, IconPlayerPlay
} from '@tabler/icons-react';

import RiskNarrativeCard from './components/RiskNarrativeCard';
import ModeSwitcher from '../components/ModeSwitcher';
import RiskAutopilotV2 from './RiskAutopilotV2';
import { RiskFormGenerator } from '../redux/selectors/riskFormGenerator';
// no context field prefill in narrative-first flow
import { uploadViaSupabase } from '../utils/supabaseUpload';
import ModelCardUploadButton from './utils/ModelCardUploadButton';
import { fetchRiskCardData } from '../redux/actions/riskActions';

/**
 * AutopilotAssessmentFlow - Main orchestrator for AI-driven risk assessment
 * Guides user through: Upload → Context → Risk Generation → Review → Report
 */
export default function AutopilotAssessmentFlow() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { modelId, assessmentUuid } = useParams();
  
  // Existing Redux state - for seamless expert mode switching
  const existingContextData = useSelector(state => 
    state.context?.data?.[modelId] || {}
  );
  const risksState = useSelector(state => state.risks.models[modelId] || {});
  const securityState = useSelector(state => state.security.models[modelId] || {});
  const modelName = risksState?.modelName || securityState?.modelName || modelId;
  
  // Get risk taxonomy from config (authoritative source for narrative generation)
  const riskFormTaxonomy = useSelector(RiskFormGenerator);

  // Step-driven workflow state managed via manageAssessment
  const [currentStep, setCurrentStep] = useState(null); // Will be loaded from backend
  const [assessmentState, setAssessmentState] = useState(null);
  const [isLoadingState, setIsLoadingState] = useState(true);
  const [uploadedBucket, setUploadedBucket] = useState(null);
  const [uploadedPath, setUploadedPath] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentAreaProgress, setCurrentAreaProgress] = useState(null);
  const [approvedNarrative, setApprovedNarrative] = useState(null);
  const [autonomous, setAutonomous] = useState(false);

  const steps = [
    { key: 'upload', label: 'Upload Model Card', icon: IconUpload },
    { key: 'context', label: 'Context Discovery', icon: IconBrain },
    { key: 'risks', label: 'Risk Analysis', icon: IconShield },
    { key: 'review', label: 'Review & Refine', icon: IconCheck },
    { key: 'report', label: 'Generate Report', icon: IconFileText }
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);
  const loadAssessmentState = useCallback(async () => {
    try {
      setIsLoadingState(true);
      const response = await fetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, operation: 'fetch' })
      });
      
      if (response.ok) {
        const state = await response.json();
        setAssessmentState(state);
        
        // Determine current step from state
        const position = state.position || {};
        if (position.step === 'completed' || position.majorStep === 'review') {
          // Assessment completed, go to canvas
          navigate(`/risk-form/${modelId}/unknown/${assessmentUuid}`);
          return;
        } else if (position.majorStep === 'risks' && position.narrativesApproved) {
          setCurrentStep('risks');
          setUploadedBucket(position.uploaded?.bucket);
          setUploadedPath(position.uploaded?.path);
        } else if (position.uploaded?.bucket && position.uploaded?.path) {
          setCurrentStep('context');
          setUploadedBucket(position.uploaded.bucket);
          setUploadedPath(position.uploaded.path);
        } else {
          setCurrentStep('upload');
        }
      } else {
        // New assessment
        setCurrentStep('upload');
      }
    } catch (error) {
      console.error('[AutopilotFlow] Failed to load state:', error);
      setCurrentStep('upload');
    } finally {
      setIsLoadingState(false);
    }
  }, [modelId, assessmentUuid, navigate]);

  // Initialize Redux model state for autopilot mode
  useEffect(() => {
    // Ensure the model is properly initialized in Redux
    // This is crucial for autopilot mode since it bypasses the normal model loading flow
    // Same initialization as copilot mode in RiskCardForm.js:114
    if (modelId && assessmentUuid && !risksState?.riskFormData) {
      console.log('[AutopilotFlow] Initializing Redux model state for:', modelId, assessmentUuid);
      dispatch(fetchRiskCardData(modelId, assessmentUuid));
    }
  }, [dispatch, modelId, assessmentUuid, risksState]);

  // Load assessment state on component mount
  useEffect(() => {
    loadAssessmentState();
  }, [loadAssessmentState]);


  /**
   * Handle model card file upload using enhanced progress system
   */
  const handleFileUpload = async (file, result = null) => {
    // If result is provided, it's from our enhanced upload system
    if (result) {
      console.log('[Autopilot] Enhanced upload completed:', result);
      setUploadedBucket(result.bucket);
      setUploadedPath(result.path);
      
      // Store extracted context data - handled by Redux
      if (result.extractedData) {
        console.log('[Autopilot] Extracted context data:', result.extractedData);
      }
      
      setCurrentStep('context');
      return;
    }

    // Legacy fallback for direct file upload (shouldn't be used now)
    if (!file) return;
    setIsProcessing(true);
    setError(null);
    try {
      console.log('[Autopilot] Starting upload...', { fileName: file.name, size: file.size, type: file.type });
      const bucket = 'model-cards';
      const path = `${modelId}/${assessmentUuid}/${file.name}`;

      // Pre-flight: check if any file already exists under this assessment and has embeddings
      const artRes = await fetch('/.netlify/functions/checkAssessmentArtifacts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid, bucket })
      });
      if (artRes.ok) {
        const info = await artRes.json();
        if (info?.found && info.embeddingsCount > 0) {
          setUploadedBucket(info.bucket);
          setUploadedPath(info.path);
          setCurrentStep('context');
          setIsProcessing(false);
          return;
        }
      }
      const { path: uploadPath } = await uploadViaSupabase({ file, bucket, path, contentType: file.type || 'application/pdf' });
      console.log('[Autopilot] Upload success', { bucket, uploadPath });
      setUploadedBucket(bucket);
      setUploadedPath(uploadPath);
      // Start Llama parsing (returns jobId quickly)
      const startRes = await fetch('/.netlify/functions/supaLlamaStart', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bucket, path: uploadPath, fileName: file.name })
      });
      if (!startRes.ok) throw new Error(await startRes.text().catch(()=> 'Start failed'));
      const { jobId } = await startRes.json();
      console.log('[Autopilot] Llama parse started', { jobId });

      // Poll finalize until succeeded (show minimal progress)
      let attempts = 0;
      while (attempts < 30) {
        const finRes = await fetch('/.netlify/functions/supaLlamaFinalize', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jobId, bucket, path: uploadPath, fileName: file.name, modelId, assessmentUuid, saveMarkdown: true })
        });
        if (finRes.status === 202) {
          const body = await finRes.json().catch(()=>({}));
          console.log('[Autopilot] Parse not ready', { status: body.status, attempts });
          // show some progress during long parsing
          if (attempts >= 1 && currentStep === 'upload') {
            // progress hint (upload+parse in progress)
          }
          attempts += 1;
          const delayMs = Math.min(5000, 1000 * Math.pow(1.5, attempts));
          console.log('[Autopilot] Waiting for parse…', { attempts, delayMs });
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          continue;
        }
        if (!finRes.ok) {
          const txt = await finRes.text().catch(()=> 'Finalize failed');
          throw new Error(txt);
        }
        const finalBody = await finRes.json().catch(()=>({}));
        console.log('[Autopilot] Finalize done', finalBody);
        break;
      }
      console.log('[Autopilot] LlamaParse finalized; starting narrative generation UI');
      // Persist upload completion to assessment state
      await fetch('/.netlify/functions/manageAssessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          assessmentUuid,
          operation: 'update',
          position: { 
            majorStep: 'context',
            uploaded: { bucket, path: uploadPath },
            stepCompleted: 'upload'
          }
        })
      });
      
      // Update UI state
      setUploadedBucket(bucket);
      setUploadedPath(uploadPath);
      setCurrentStep('context');
    } catch (err) {
      setError(`Failed to process model card: ${err.message}`);
      console.error('[Autopilot] Error during upload/process:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // removed extraction-based gap flow in favor of narrative-first

 
  /**
   * Enhanced model card deletion with proper UX
   */
  const handleDeleteModelCard = async () => {
    setIsDeleting(true);
    setError(null);
    
    try {
      // Try to find any existing artifact and delete it
      const res = await fetch('/.netlify/functions/checkAssessmentArtifacts', {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid, bucket: 'model-cards' })
      });
      
      if (res.ok) {
        const info = await res.json();
        if (info?.found) {
          const del = await fetch('/.netlify/functions/deleteModelCardSupabase', {
            method: 'POST', 
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ bucket: info.bucket, path: info.path })
          });
          
          if (!del.ok) {
            throw new Error(await del.text().catch(() => 'Delete failed'));
          }
          
          // Reset state after successful deletion
          setUploadedBucket(null);
          setUploadedPath(null);
          setCurrentStep('upload');
        }
      }
      
      setDeleteModalOpen(false);
    } catch (e) {
      setError(`Failed to delete: ${e.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Switch to co-pilot mode with existing data
   */
  const handleSwitchToCopilotMode = () => {
    // Navigate to existing co-pilot workflow (expert mode with AI assistance)
    navigate(`/risk-form/${modelId}/unknown/${assessmentUuid}`);
  };

  // (removed client-side text reader; backend handles extraction)

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Clean Header - Model Focus */}
        <Card withBorder p="md">
          <Group justify="space-between" align="center">
            <div>
              <Text size="lg" fw={700} c="blue">
                {modelName || modelId} Assessment
              </Text>
            </div>
            
            {/* Delete button moved to far right */}
            {uploadedBucket && uploadedPath && (
              <Tooltip label="Delete assessment" withArrow>
                <ActionIcon 
                  variant="subtle" 
                  color="red" 
                  size="md"
                  onClick={() => setDeleteModalOpen(true)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        </Card>


        {/* Enhanced Error Alert */}
        {error && (
          <Alert 
            icon={<IconAlertCircle size="1rem" />} 
            title="Something went wrong" 
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setError(null)}
            style={{ maxWidth: '600px', margin: '0 auto' }}
          >
            <Text size="sm">
              {error}
            </Text>
            <Text size="xs" c="dimmed" mt="xs">
              You can try uploading again or switch to Co-pilot mode for manual control.
            </Text>
          </Alert>
        )}

        {/* Loading State */}
        {isLoadingState && (
          <Card withBorder radius="md" p="xl">
            <Group justify="center" gap="md">
              <IconLoader size={24} className="rotate-animation" />
              <Text>Loading assessment state...</Text>
            </Group>
          </Card>
        )}

        {/* Step Content */}
        {!isLoadingState && (
          <Transition mounted transition="fade" duration={300}>
            {(styles) => (
              <div style={styles}>
                {currentStep === 'upload' && (
                <UploadStep
                  onFileUpload={handleFileUpload}
                  isProcessing={isProcessing}
                  existingData={Object.keys(existingContextData).length > 0}
                  assessmentState={assessmentState}
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  onResumeAssessment={(resumeStep) => {
                    console.log('[Autopilot] Resuming assessment from step:', resumeStep);
                    setCurrentStep(resumeStep);
                  }}
                  onUseExisting={() => {
                    // Open the same Assessment Canvas as co-pilot uses (embedded)
                    navigate(`/risk-form/${modelId}/unknown/${assessmentUuid}`);
                  }}
                />
              )}
              
              {currentStep === 'context' && (
                <RiskNarrativeCard
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  bucket={uploadedBucket}
                  path={uploadedPath}
                  riskFormData={riskFormTaxonomy}
                  onSwitchToCopilot={handleSwitchToCopilotMode}
                  onApproved={async (narrative) => {
                    console.log('[Autopilot] All narratives approved; starting risk automation');
                    setApprovedNarrative(narrative); // Store the approved narrative
                    try {
                      // Persist narrative approval to assessment state
                      await fetch('/.netlify/functions/manageAssessment', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          modelId,
                          assessmentUuid,
                          operation: 'update',
                          position: { 
                            majorStep: 'risks',
                            uploaded: { bucket: uploadedBucket, path: uploadedPath },
                            narrativesApproved: true,
                            stepCompleted: 'context'
                          }
                        })
                      });
                    } catch (e) {
                      console.error('[Autopilot] Failed to persist narrative approval:', e);
                    }
                    setCurrentStep('risks');
                  }}
                />
              )}
              
              {currentStep === 'risks' && (
                <Stack gap="md">
                  {/* Assessment Tracker Card with Ring, Badges, and Mode Toggles */}
                  <Card withBorder p="md" mb="lg">
                    <Stack gap="md">
                      {/* Header */}
                      <Text size="md" fw={600}>Assessment Tracker</Text>
                      
                      {/* Two-Section Layout with Proper 2/3 - 1/3 Split */}
                      <Group align="flex-start" wrap="nowrap" style={{ width: '100%' }}>
                        {/* Left Section: Ring Progress and Badges - 2/3 space */}
                        <Stack gap="md" style={{ flex: 2, minWidth: 0 }}>
                          {/* Ring Progress and Current Area */}
                          <Group gap="md" align="center">
                            <RingProgress
                              size={60}
                              thickness={6}
                              sections={[
                                { 
                                  value: currentAreaProgress ? (currentAreaProgress.currentIndex / currentAreaProgress.total) * 100 : 0, 
                                  color: 'blue' 
                                }
                              ]}
                              label={
                                <Text size="xs" fw={700} ta="center">
                                  {currentAreaProgress ? `${currentAreaProgress.currentIndex}/${currentAreaProgress.total}` : '0/7'}
                                </Text>
                              }
                            />
                            <div>
                              <Text size="sm" fw={500}>
                                {currentAreaProgress 
                                  ? `Current: ${currentAreaProgress.currentArea}` 
                                  : 'Ready to begin risk analysis'
                                }
                              </Text>
                            </div>
                          </Group>
                          
                          {/* Assessment Phase Badges */}
                          <Group gap="xs" wrap="wrap">
                            {steps.map((step, index) => {
                              const isActive = index === currentStepIndex;
                              const isCompleted = index < currentStepIndex;
                              const StepIcon = step.icon;
                              
                              return (
                                <Badge 
                                  key={step.key}
                                  size="sm"
                                  variant={isActive ? "filled" : isCompleted ? "light" : "outline"}
                                  color={isCompleted ? 'green' : isActive ? 'blue' : 'gray'}
                                  leftSection={<StepIcon size={12} />}
                                >
                                  {step.label.replace(' Model Card', '').replace(' Discovery', '').replace(' & Refine', '')}
                                </Badge>
                              );
                            })}
                          </Group>
                        </Stack>
                        
                        {/* Gentle Divider */}
                        <Divider orientation="vertical" size="xs" color="gray.3" style={{ margin: '0 12px' }} />
                        
                        {/* Right Section: Assessment Mode Controls - 1/3 space */}
                        <Stack gap="md" style={{ flex: 1, minWidth: 120 }} align="center">
                          <Text size="sm" c="dimmed" fw={500}>Assessment Mode</Text>
                          
                          <Stack gap="sm" align="center" style={{ width: '100%' }}>
                            {/* Auto/Co-pilot Toggle */}
                            <ModeSwitcher
                              currentMode="autopilot"
                              onModeChange={(mode) => mode === 'copilot' && handleSwitchToCopilotMode()}
                              showAsSwitch={true}
                            />
                            
                            {/* Supervised/Autonomous Toggle - Same style as ModeSwitcher */}
                            <Group gap="xs" align="center" style={{ 
                              padding: '4px 8px', 
                              borderRadius: '6px', 
                              backgroundColor: 'var(--mantine-color-gray-0)',
                              width: '100%',
                              justifyContent: 'space-between'
                            }}>
                              <Text size="xs" c={!autonomous ? "blue" : "dimmed"} fw={!autonomous ? 600 : 400}>
                                Supervised
                              </Text>
                              <Switch
                                checked={autonomous}
                                onChange={(event) => {
                                  const newValue = event.currentTarget.checked;
                                  console.log(`[AutopilotFlow] 🤖 AUTONOMOUS SWITCH TOGGLED: ${autonomous} -> ${newValue}`);
                                  setAutonomous(newValue);
                                }}
                                color="green"
                                size="sm"
                                styles={{
                                  track: { backgroundColor: autonomous ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-gray-4)' }
                                }}
                              />
                              <Text size="xs" c={autonomous ? "green" : "dimmed"} fw={autonomous ? 600 : 400}>
                                Autonomous
                              </Text>
                            </Group>
                          </Stack>
                        </Stack>
                      </Group>
                    </Stack>
                  </Card>

                  <RiskAutopilotV2
                    modelId={modelId}
                    assessmentUuid={assessmentUuid}
                    bucket={uploadedBucket}
                    path={uploadedPath}
                    approvedNarrative={approvedNarrative}
                    autonomous={autonomous}
                    onAreaProgress={setCurrentAreaProgress}
                    onComplete={async () => {
                    console.log('[Autopilot] Complete risk analysis (all phases); transitioning to review');
                    setCurrentAreaProgress(null); // Clear area progress
                    try {
                      // Persist risk completion
                      await fetch('/.netlify/functions/manageAssessment', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          modelId,
                          assessmentUuid,
                          operation: 'update',
                          position: { 
                            majorStep: 'review',
                            stepCompleted: 'risks',
                            uploaded: { bucket: uploadedBucket, path: uploadedPath },
                            narrativesApproved: true
                          }
                        })
                      });
                    } catch (e) {
                      console.error('[Autopilot] Failed to persist risk completion:', e);
                    }
                    setCurrentStep('review');
                  }}
                  />
                </Stack>
              )}
              
              {currentStep === 'review' && (
                <ReviewStep
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                  onSwitchToCopilot={handleSwitchToCopilotMode}
                  onGenerateReport={() => setCurrentStep('report')}
                />
              )}
              
              {currentStep === 'report' && (
                <ReportStep
                  modelId={modelId}
                  assessmentUuid={assessmentUuid}
                />
              )}
              </div>
            )}
          </Transition>
        )}

        {/* Elegant Deletion Confirmation Modal */}
        <Modal
          opened={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          title={
            <Group gap="sm">
              <IconTrash size={20} color="#e03131" />
              <Text fw={600}>Delete Model Card</Text>
            </Group>
          }
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              This will permanently delete your uploaded model card and all associated embeddings. 
              You'll need to re-upload to continue your assessment.
            </Text>
            
            <Group justify="flex-end" gap="sm">
              <Button 
                variant="subtle" 
                onClick={() => setDeleteModalOpen(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button 
                color="red" 
                loading={isDeleting}
                onClick={handleDeleteModelCard}
                leftSection={<IconTrash size={16} />}
              >
                Delete Model Card
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Container>
  );
}

/**
 * Upload Step Component
 */
function UploadStep({ 
  onFileUpload, 
  isProcessing, 
  existingData, 
  assessmentState,
  onResumeAssessment,
  onUseExisting,
  modelId,
  assessmentUuid, 
  continueLabel = 'Continue with existing assessment data' 
}) {
  return (
    <Card withBorder radius="md" p="xl">
      <Stack gap="lg" align="center">
        <Stack gap="sm" align="center">
          <Group gap="md" align="center">
            <IconUpload size={64} color="#1976d2" />
            <Stack gap="xs" align="flex-start">
              <Text size="xl" fw={600}>Upload Your Model Documentation</Text>
              <Text size="sm" c="dimmed" maw={400}>
                Drop your model card, documentation, or system specs. RiskGPT will analyze it 
                and automatically generate a comprehensive risk assessment.
              </Text>
            </Stack>
          </Group>
        </Stack>

        <Stack gap="md" align="center">
          <ModelCardUploadButton
            modelId={modelId}
            assessmentUuid={assessmentUuid}
            onSuccess={(result) => {
              onFileUpload(null, result); // Pass result to existing handler
            }}
            onError={(error) => {
              console.error('[Autopilot] Upload error:', error);
            }}
            buttonText="Choose Model Card"
            size="lg"
          />
        </Stack>

        <Text size="sm" c="dimmed">
          Supported: PDF, JSON, Markdown, Text, Word documents
        </Text>

        {/* Resume Assessment for Returning Users */}
        {assessmentState?.position?.uploaded && (
          <>
            <Divider label="Continue Assessment" labelPosition="center" w="100%" />
            <Card withBorder p="md" bg="blue.0" style={{ maxWidth: '500px' }}>
              <Stack gap="sm">
                <Group gap="sm">
                  <IconPlayerPlay size={20} color="#1976d2" />
                  <Text fw={600} c="blue">
                    Assessment in Progress
                  </Text>
                </Group>
                <Text size="sm" c="dimmed">
                  You have an ongoing assessment. Resume where you left off or start fresh.
                </Text>
                <Group gap="sm" justify="center">
                  {assessmentState.position.narrativesApproved ? (
                    <Button 
                      variant="filled" 
                      onClick={() => onResumeAssessment('risks')}
                      leftSection={<IconPlayerPlay size={16} />}
                    >
                      Resume Risk Analysis
                    </Button>
                  ) : (
                    <Button 
                      variant="filled" 
                      onClick={() => onResumeAssessment('context')}
                      leftSection={<IconPlayerPlay size={16} />}
                    >
                      Resume Narrative Review
                    </Button>
                  )}
                  <Button variant="subtle" size="sm">
                    Start Fresh
                  </Button>
                </Group>
              </Stack>
            </Card>
          </>
        )}

        {existingData && !assessmentState?.position?.uploaded && (
          <>
            <Divider label="or" labelPosition="center" w="100%" />
            <Button variant="light" onClick={onUseExisting}>
              {continueLabel}
            </Button>
          </>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Assessment Overview/Collage Dashboard
 */
function ReviewStep({ modelId, assessmentUuid, onSwitchToCopilot, onGenerateReport }) {
  const risksState = useSelector(state => state.risks.models[modelId] || {});
  const riskFormData = risksState.riskFormData || {};
  const savedControls = useSelector(state => state.controls.savedControls || {});
  const residualRiskState = useSelector(state => state.residualRisk.byModel[modelId] || {});
  const securityState = useSelector(state => state.security.models[modelId] || {});

  const areas = Object.keys(riskFormData);

  // Check if security assessment is complete (has scenarios)
  const securityComplete = securityState.scenarios && Object.keys(securityState.scenarios).length > 0;

  // Area status calculation - removed getAreaStats as area cards are no longer displayed
  const completedAreas = areas.filter(areaName => {
    const areaRisks = riskFormData[areaName]?.sectionRisks || {};
    const hasRisks = Object.values(areaRisks).some(risks =>
      Array.isArray(risks) && risks.some(risk => risk.status === 'Saved')
    );

    if (hasRisks) {
      const hasEvaluations = Object.values(areaRisks).some(risks =>
        Array.isArray(risks) && risks.some(risk => risk.priority)
      );
      const hasActions = Object.values(areaRisks).some(risks =>
        Array.isArray(risks) && risks.some(risk => savedControls[risk.riskId]?.length > 0)
      );
      return hasEvaluations && hasActions;
    }
    return false;
  }).length;

  const partialAreas = areas.filter(areaName => {
    const areaRisks = riskFormData[areaName]?.sectionRisks || {};
    const hasRisks = Object.values(areaRisks).some(risks =>
      Array.isArray(risks) && risks.some(risk => risk.status === 'Saved')
    );

    if (hasRisks) {
      const hasEvaluations = Object.values(areaRisks).some(risks =>
        Array.isArray(risks) && risks.some(risk => risk.priority)
      );
      const hasActions = Object.values(areaRisks).some(risks =>
        Array.isArray(risks) && risks.some(risk => savedControls[risk.riskId]?.length > 0)
      );
      return !(hasEvaluations && hasActions);
    }
    return false;
  }).length;

  // Add security to completed count if it has scenarios
  const totalCompletedAreas = completedAreas + (securityComplete ? 1 : 0);
  const totalAreas = areas.length + 1; // 6 risk areas + 1 security area

  return (
    <Stack gap="lg">

      {/* Assessment Summary & Report */}
      <ResidualRiskSummary
        modelId={modelId}
        riskFormData={riskFormData}
        savedControls={savedControls}
        residualRiskState={residualRiskState}
        completedAreas={totalCompletedAreas}
        partialAreas={partialAreas}
        totalAreas={totalAreas}
        onGenerateReport={onGenerateReport}
      />

{/* Old Assessment Summary section removed - functionality moved to ResidualRiskSummary component above */}
    </Stack>
  );
}

/**
 * Report Step Component - Embedded Assessment Canvas
 */
function ReportStep({ modelId, assessmentUuid }) {
  // Mark assessment as complete when report step is reached
  useEffect(() => {
    const markAssessmentComplete = async () => {
      try {
        // Update position
        await fetch('/.netlify/functions/manageAssessment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            modelId,
            operation: 'update',
            position: {
              step: 'completed',
              majorStep: 'review',
              completedAt: new Date().toISOString(),
              source: 'autopilot'
            }
          })
        });
        
        // Update status to 'Completed' (same format as co-pilot)
        await fetch('/.netlify/functions/manageAssessment', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            modelId,
            assessmentUuid,
            operation: 'update',
            assessmentStatus: 'Completed'
          })
        });
        
        console.log('Assessment position and status marked as complete');
      } catch (error) {
        console.error('Failed to mark assessment as complete:', error);
      }
    };
    
    markAssessmentComplete();
  }, [modelId, assessmentUuid]);

  // Import AssessmentCanvas dynamically to avoid circular imports
  const AssessmentCanvas = React.lazy(() => import('../canvas/AssessmentCanvas'));

  return (
    <React.Suspense fallback={
      <Card withBorder radius="md" p="xl">
        <Text>Loading assessment canvas...</Text>
      </Card>
    }>
      <AssessmentCanvas modelId={modelId} assessmentUuid={assessmentUuid} />
    </React.Suspense>
  );
}

/**
 * Assessment Summary & Report Component
 */
function ResidualRiskSummary({ modelId, riskFormData, savedControls, residualRiskState, completedAreas, partialAreas, totalAreas, onGenerateReport }) {
  // Get all risks with residual assessments
  const risksWithResidual = [];
  
  Object.entries(riskFormData).forEach(([areaName, areaData]) => {
    const sectionRisks = areaData?.sectionRisks || {};
    Object.entries(sectionRisks).forEach(([sourceName, risks]) => {
      if (Array.isArray(risks)) {
        risks.forEach(risk => {
          if (risk.status === 'Saved' && residualRiskState[risk.riskId]) {
            const residualData = residualRiskState[risk.riskId];
            const controls = savedControls[risk.riskId] || [];
            risksWithResidual.push({
              ...risk,
              areaName,
              sourceName,
              controls: controls.length,
              residualData
            });
          }
        });
      }
    });
  });

  if (risksWithResidual.length === 0) {
    return null;
  }

  // Risk priority colors consistent with RiskLandscape.js
  const riskPriorityColors = {
    Critical: '#1A1A1A',    // Black
    High: '#993333',        // Red
    Significant: '#B94D4D', // Amber
    Moderate: '#D4AA7D',    // Light Orange
    Low: '#B8C2B7',         // Blue
    Negligible: '#D8D8D8',  // Green
    None: '#EBEBEB'         // Light Grey
  };

  const getRiskColor = (rating) => {
    const capitalizedRating = rating?.charAt(0).toUpperCase() + rating?.slice(1).toLowerCase();
    return riskPriorityColors[capitalizedRating] || '#EBEBEB';
  };

  return (
    <Card withBorder p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap="sm" align="center" style={{ flex: 2 }}>
            <Text size="lg" fw={600}>
              📊 Assessment Summary & Report
            </Text>
            <Group gap="xs" wrap="wrap">
              <Badge color="green" size="sm">
                {completedAreas} Completed
              </Badge>
              <Badge color="orange" size="sm">
                {partialAreas} Partial
              </Badge>
              <Badge color="gray" size="sm">
                {totalAreas - completedAreas - partialAreas} Pending
              </Badge>
              <Badge size="sm" variant="light" color="blue">
                {risksWithResidual.length} Risks Assessed
              </Badge>
            </Group>
          </Group>
          <div style={{ flex: 1 }}>
            <Group justify="flex-end">
              <Button onClick={onGenerateReport} disabled={completedAreas === 0} size="sm">
                📄 Generate Report
              </Button>
            </Group>
          </div>
        </Group>
        
        <Text size="sm" c="dimmed">
          Post-mitigation risk levels after implementing recommended controls and actions.
        </Text>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '1rem' 
        }}>
          {risksWithResidual.map((risk) => {
            const residualRating = risk.residualData.residual_rating || 'Unknown';
            const actionPlanRating = risk.residualData.action_plan_rating || 'N/A';
            
            return (
              <Card key={risk.riskId} withBorder p="md" bg="gray.0">
                <Stack gap="sm">
                  <Group justify="space-between" align="flex-start">
                    <div style={{ flex: 1 }}>
                      <Text fw={600} size="sm" lineClamp={2}>
                        {risk.riskName}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {risk.areaName} • {risk.sourceName}
                      </Text>
                    </div>
                    <Badge 
                      style={{ backgroundColor: getRiskColor(residualRating), color: 'white' }}
                      variant="filled"
                      size="sm"
                    >
                      {residualRating?.charAt(0).toUpperCase() + residualRating?.slice(1).toLowerCase()}
                    </Badge>
                  </Group>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr 1fr', 
                    gap: '0.5rem' 
                  }}>
                    <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                      <Text size="xs" fw={600} c="blue">{risk.priority?.charAt(0).toUpperCase() + risk.priority?.slice(1).toLowerCase() || 'N/A'}</Text>
                      <Text size="xs" c="dimmed">Initial</Text>
                    </Box>
                    <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                      <Text size="xs" fw={600} c="orange">{risk.controls}</Text>
                      <Text size="xs" c="dimmed">Actions</Text>
                    </Box>
                    <Box ta="center" p="xs" bg="white" style={{ borderRadius: 4 }}>
                      <Text size="xs" fw={600} c="green">{actionPlanRating}</Text>
                      <Text size="xs" c="dimmed">Plan Rating</Text>
                    </Box>
                  </div>

                  {risk.residualData.action_plan_effectiveness && (
                    <Text size="xs" c="dimmed" style={{ 
                      backgroundColor: 'white', 
                      padding: '8px', 
                      borderRadius: '4px',
                      borderLeft: `3px solid var(--mantine-color-${getRiskColor(residualRating)}-6)`
                    }}>
                      {risk.residualData.action_plan_effectiveness.slice(0, 120)}...
                    </Text>
                  )}
                </Stack>
              </Card>
            );
          })}
        </div>
      </Stack>
    </Card>
  );
}